import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * One badge shape for the whole app — the Confluence review called out the
 * "Christmas tree" effect where statuses were sometimes plain coloured text,
 * sometimes pill, sometimes uppercase. Every status/reason/label now renders
 * through this: 20px tall, 4px radius, tinted fill + matching 1px border.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-sm border px-1.5 h-5 text-[11.5px] font-medium whitespace-nowrap',
  {
    variants: {
      variant: {
        /** Neutral — reason codes, counts, anything without a semantic state. */
        neutral: 'border-border bg-secondary text-zinc-700',
        success: 'border-green-200 bg-green-50 text-green-700',
        danger: 'border-red-200 bg-red-50 text-red-700',
        warning: 'border-amber-200 bg-amber-50 text-amber-800',
        info: 'border-sky-200 bg-sky-50 text-sky-700',
        outline: 'border-border bg-background text-muted-foreground',
      },
    },
    defaultVariants: { variant: 'neutral' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Renders the design's 5px leading status dot. */
  dot?: string;
}

function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && <span className={cn('h-[5px] w-[5px] shrink-0 rounded-full', dot)} />}
      {children}
    </span>
  );
}

export { Badge, badgeVariants };
